import { doc, setDoc, getDoc, Timestamp, getFirestore } from 'firebase/firestore';
import type { User } from '@/types';

/**
 * Creates a user profile document in Firestore
 * Called after successful Firebase Authentication registration
 */
export async function createUserProfile(userId: string, email: string): Promise<void> {
  const db = getFirestore();
  const userRef = doc(db, 'users', userId);

  // Check if profile already exists
  const userSnap = await getDoc(userRef);
  if (userSnap.exists()) {
    // Profile already exists, nothing to do
    return;
  }

  // Create new user profile per data-model.md
  const userProfile = {
    email,
    createdAt: Timestamp.fromDate(new Date()),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    preferences: {
      units: 'metric' as const,
    },
  };

  await setDoc(userRef, userProfile);
}

/**
 * Retrieves a user profile by ID
 */
export async function getUserProfile(userId: string): Promise<User> {
  const db = getFirestore();
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists()) {
    throw new Error('User profile not found');
  }

  const data = userSnap.data();
  return {
    id: userSnap.id,
    email: data.email,
    createdAt: data.createdAt.toDate(),
    timezone: data.timezone,
    preferences: data.preferences,
  };
}

/**
 * Updates a user profile
 */
export async function updateUserProfile(
  userId: string,
  updates: Partial<Omit<User, 'id' | 'email' | 'createdAt'>>
): Promise<void> {
  const db = getFirestore();
  const userRef = doc(db, 'users', userId);
  await setDoc(userRef, updates, { merge: true });
}
